import localize from "@vueform/vueform/src/utils/localize";

// Whitelist of formatting tags allowed in twig field output. Anything else is
// escaped so values like "We <3 Techno" don't get mangled by the v-html parser.
const TWIG_ALLOWED_TAGS = [
    'br', 'hr',
    'b', 'strong', 'i', 'em', 'u', 'small', 'sub', 'sup',
    'p', 'span', 'div',
    'ul', 'ol', 'li',
    'a',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
    'img',
];

// Whitelist of attributes preserved on whitelisted tags. URL-bearing attrs
// (href, src) are additionally protocol-filtered below.
const TWIG_ALLOWED_ATTRS = [
    'class', 'style', 'title',
    'href', 'target', 'rel',
    'src', 'alt', 'width', 'height',
    'colspan', 'rowspan', 'scope',
];

// Replace unsafe URL protocols (javascript:, vbscript:, non-image data:) with
// a harmless '#'. Operates on already-decoded attribute strings.
function filterUrlAttrs(attrs) {
    return attrs.replace(
        /(\s+)(href|src)\s*=\s*"([^"]*)"/gi,
        function (_m, ws, name, value) {
            const lowered = value.toLowerCase().trim();
            if (lowered.indexOf('javascript:') === 0 || lowered.indexOf('vbscript:') === 0) {
                return ws + name + '="#"';
            }
            if (lowered.indexOf('data:') === 0 && lowered.indexOf('data:image/') !== 0) {
                return ws + name + '="#"';
            }
            return _m;
        }
    );
}

function sanitizeTwigHtml(value) {
    if (value === undefined || value === null) return '';
    let s = String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    const tagAlt = TWIG_ALLOWED_TAGS.join('|');
    const attrAlt = TWIG_ALLOWED_ATTRS.join('|');
    // Closing tags: &lt;/tag&gt;
    s = s.replace(new RegExp('&lt;\\/(' + tagAlt + ')\\s*&gt;', 'gi'), '</$1>');
    // Opening / self-closing tags. Attribute value: lazily match anything
    // (already-escaped) until the closing &quot;. Using .*? is safe here because
    // the input is fully HTML-escaped at this point — no raw '<', '>', or '"'
    // can appear inside.
    const attrPart = '(?:\\s+(?:' + attrAlt + ')\\s*=\\s*&quot;.*?&quot;)*';
    s = s.replace(
        new RegExp('&lt;(' + tagAlt + ')(' + attrPart + ')\\s*(\\/?)&gt;', 'gi'),
        function (_m, tag, attrs, slash) {
            const decodedAttrs = filterUrlAttrs(attrs ? attrs.replace(/&quot;/g, '"') : '');
            return '<' + tag + decodedAttrs + (slash ? '/' : '') + '>';
        }
    );
    return s;
}

export default {
    methods : {
        transformTabNavIfNeeded() {
            // Tab navigation is now handled by custom FormTabs/FormTab templates
        },
        // Twig (calculation) fields arrive from the backend as text inputs with
        // originalType==='twig'. Convert them to static elements so the rendered
        // value (which may contain HTML) is shown read-only. Values are passed
        // through sanitizeTwigHtml() to keep formatting tags like <br> while
        // escaping bare '<' that isn't part of a whitelisted tag.
        //
        // html display fields (type==='html') are static HTML blocks configured by
        // the form admin. They must also render as static elements — bypassing
        // Vueform's EditorElement (Trix), whose built-in HTML sanitizer strips
        // attributes like target="_blank" that are not in its allowlist.
        applyTwigFieldFixes(schema, data, isItemTemplate = false) {
            if (!schema || typeof schema !== 'object') return;
            Object.keys(schema).forEach((name) => {
                const node = schema[name];
                if (!node || typeof node !== 'object') return;

                if (node.type === 'html') {
                    node.type = 'static';
                    node.allowHtml = true;
                    node.content = node.html || '';
                    delete node.html;
                }

                if (node.originalType === 'twig') {
                    node.type = 'static';
                    node.allowHtml = true;

                    if (isItemTemplate) {
                        const fieldName = name;
                        node.content = (el$) => {
                            const value = el$ && el$.parent && el$.parent.value
                                ? el$.parent.value[fieldName] : undefined;
                            return sanitizeTwigHtml(value);
                        };
                    } else {
                        node.content = sanitizeTwigHtml(data ? data[name] : undefined);
                    }
                }

                if (node.schema) {
                    this.applyTwigFieldFixes(node.schema, data ? data[name] : undefined, isItemTemplate);
                }
                if (node.object && node.object.schema) {
                    this.applyTwigFieldFixes(node.object.schema, null, true);
                }
            });
        },
        // Vueform's checkbox template never mounts the generic label the
        // required asterisk (*) is normally rendered into, so required
        // checkboxes silently lacked the same hint every other required field
        // type shows. php-core emits the field's actual name/label as `before`
        // (rendered above the checkbox, the equivalent of the "label" other
        // field types show the asterisk next to) and the inline caption next
        // to the checkbox itself as `text` — append the asterisk to `before`
        // to match, falling back to `text` for plain consent-style checkboxes
        // that have no separate field name (only a caption).
        markRequiredCheckboxes(schema) {
            if (!schema || typeof schema !== 'object') return;
            const appendAsterisk = (value) => {
                if (typeof value === 'object') {
                    Object.keys(value).forEach((locale) => {
                        if (value[locale]) value[locale] += ' *';
                    });
                    return value;
                }
                return value + ' *';
            };
            Object.keys(schema).forEach((name) => {
                const node = schema[name];
                if (!node || typeof node !== 'object') return;

                if (node.type === 'checkbox') {
                    const rules = Array.isArray(node.rules) ? node.rules : String(node.rules || '').split('|');
                    if (rules.includes('required')) {
                        if (node.before) {
                            node.before = appendAsterisk(node.before);
                        } else if (node.text) {
                            node.text = appendAsterisk(node.text);
                        }
                    }
                }

                if (node.schema) {
                    this.markRequiredCheckboxes(node.schema);
                }
                if (node.object && node.object.schema) {
                    this.markRequiredCheckboxes(node.object.schema);
                }
            });
        },
        checkInvalidTabs(mutationsList) {
            if (mutationsList.filter(tab => (tab.target.classList.contains('vf-tab-wrapper-invalid') && !(tab.target.classList.contains('vf-tab-wrapper-active')))).length) {
                const errors = window.vueform.tabs$.tabs$Array.filter(tab => tab.invalid).map(tab => {
                    return localize(tab.label, this.$vueform, window.vueform);
                });
                if (errors.length) {
                    const errorText = errors.length === 1 ? errors[0] : (': <ul><li>'+errors.join('</li><li>')+'</li></ul>');
                    UIkit.modal.alert('<span class="uk-text-danger">'+window.vueform.translations.festiware.errors.tab_hint+ '<strong> '+ errorText+'</strong></span>', {i18n: window.vueform.translations.festiware.errors.tab_hint_buttons})
                }
            }
        }
    }
}
